/**
 * Iso-value contour lines (iso-luminance / isocandela style) by marching
 * squares on the pixel-centre grid, with saddle disambiguation by the cell mean.
 */
import type { Plane } from './types';

export interface ContourLevel {
  level: number;
  /** Flat list of segments x0,y0,x1,y1 in image coordinates. */
  segments: Float32Array;
}

/**
 * Compute contours for the given levels. `stride` > 1 subsamples the grid
 * (for very large images); coordinates are still returned in full-image pixels.
 */
export function marchingSquares(p: Plane, levels: number[], stride = 1): ContourLevel[] {
  const s = Math.max(1, Math.floor(stride));
  const W = p.width;
  const H = p.height;
  const gw = Math.floor((W - 1) / s) + 1;
  const gh = Math.floor((H - 1) / s) + 1;
  const val = (gx: number, gy: number) => p.data[gy * s * W + gx * s];
  const out: ContourLevel[] = [];
  for (const L of levels) {
    const seg: number[] = [];
    const px = (gx: number) => p.ox + gx * s + 0.5;
    const py = (gy: number) => p.oy + gy * s + 0.5;
    for (let gy = 0; gy < gh - 1; gy++) {
      for (let gx = 0; gx < gw - 1; gx++) {
        const a = val(gx, gy); // top-left
        const b = val(gx + 1, gy); // top-right
        const c = val(gx + 1, gy + 1); // bottom-right
        const d = val(gx, gy + 1); // bottom-left
        if (!(Number.isFinite(a) && Number.isFinite(b) && Number.isFinite(c) && Number.isFinite(d))) continue;
        const code = (a >= L ? 8 : 0) | (b >= L ? 4 : 0) | (c >= L ? 2 : 0) | (d >= L ? 1 : 0);
        if (code === 0 || code === 15) continue;
        const x0 = px(gx);
        const x1 = px(gx + 1);
        const y0 = py(gy);
        const y1 = py(gy + 1);
        const t = (u: number, v: number) => (u === v ? 0.5 : (L - u) / (v - u));
        // Edge intersection points.
        const top = () => [x0 + t(a, b) * (x1 - x0), y0];
        const right = () => [x1, y0 + t(b, c) * (y1 - y0)];
        const bottom = () => [x0 + t(d, c) * (x1 - x0), y1];
        const left = () => [x0, y0 + t(a, d) * (y1 - y0)];
        const add = (p1: number[], p2: number[]) => seg.push(p1[0], p1[1], p2[0], p2[1]);
        switch (code) {
          case 1:
          case 14:
            add(left(), bottom());
            break;
          case 2:
          case 13:
            add(bottom(), right());
            break;
          case 3:
          case 12:
            add(left(), right());
            break;
          case 4:
          case 11:
            add(top(), right());
            break;
          case 6:
          case 9:
            add(top(), bottom());
            break;
          case 7:
          case 8:
            add(left(), top());
            break;
          case 5:
          case 10: {
            const centerHigh = (a + b + c + d) / 4 >= L;
            // code 5: b and d high; code 10: a and c high.
            const connectAC = code === 10 ? centerHigh : !centerHigh;
            if (connectAC) {
              // The high corners a,c are connected through the centre: cut off b and d.
              add(top(), right());
              add(left(), bottom());
            } else {
              add(left(), top());
              add(bottom(), right());
            }
            break;
          }
        }
      }
    }
    out.push({ level: L, segments: new Float32Array(seg) });
  }
  return out;
}

/** Evenly spaced contour levels between lo and hi (exclusive), linear or logarithmic. */
export function autoLevels(lo: number, hi: number, count: number, log: boolean): number[] {
  const n = Math.max(1, Math.round(count));
  if (log && lo > 0 && hi > lo) {
    const a = Math.log10(lo);
    const b = Math.log10(hi);
    return Array.from({ length: n }, (_, i) => Math.pow(10, a + ((i + 1) * (b - a)) / (n + 1)));
  }
  return Array.from({ length: n }, (_, i) => lo + ((i + 1) * (hi - lo)) / (n + 1));
}
